import type { Column } from '@shared/types/data-table'
import type { CardProgram } from '@features/programs/types/card-program.types'
import type { ColumnManagementConfig } from '@shared/lib/tables/utils/column-management'
import { createStatusColumn, createDateColumn, createTextColumn, createCustomColumn, createCurrencyColumn } from './column-factories'
import { NetworkLogo } from '@shared/components/ui/NetworkLogo'

const typeLabels: Record<string, string> = {
  credit: 'Credit',
  debit: 'Debit',
  prepaid: 'Prepaid',
  commercial: 'Commercial',
  virtual: 'Virtual',
}

export const CARD_PROGRAM_COLUMNS: Record<string, Column<CardProgram>> = {
  name: createTextColumn<CardProgram>('name', 'Program Name', 220),
  status: createStatusColumn<CardProgram>('card-program'),
  type: createTextColumn<CardProgram>(
    'type', 'Type', 130, undefined, undefined,
    (value: string) => typeLabels[value] || value
  ),
  network: createCustomColumn<CardProgram>(
    'network', 'Network', 120,
    (item) => <NetworkLogo network={item.network} />
  ),
  bankName: createTextColumn<CardProgram>('bankName', 'Bank', 180),
  currency: createTextColumn<CardProgram>('currency', 'Currency', 90),
  cardCount: createTextColumn<CardProgram>(
    'cardCount' as keyof CardProgram, 'Cards Issued', 120, undefined, undefined,
    (value: number) => value?.toLocaleString() ?? '0'
  ),
  spendLimit: createCurrencyColumn<CardProgram>('spendLimit', 'Spend Limit', 130),
  totalVolume: createCurrencyColumn<CardProgram>('totalVolume', 'Total Volume', 140),
  launchDate: createDateColumn<CardProgram>('launchDate', 'Launch Date', 120),
}

export const CARD_PROGRAM_STATUS_CONFIG = {
  all: ['name', 'status', 'type', 'network', 'bankName', 'cardCount', 'totalVolume', 'launchDate'],
  active: ['name', 'status', 'type', 'network', 'bankName', 'cardCount', 'totalVolume', 'launchDate'],
  draft: ['name', 'status', 'type', 'network', 'bankName', 'launchDate'],
  pending: ['name', 'status', 'type', 'network', 'bankName', 'launchDate'],
  suspended: ['name', 'status', 'type', 'network', 'bankName', 'cardCount', 'launchDate'],
  inactive: ['name', 'status', 'type', 'network', 'bankName', 'launchDate'],
} as const

export const getCardProgramColumns = (status: string = 'all'): Column<CardProgram>[] => {
  const keys = CARD_PROGRAM_STATUS_CONFIG[status as keyof typeof CARD_PROGRAM_STATUS_CONFIG] || CARD_PROGRAM_STATUS_CONFIG.all
  return keys.map(key => CARD_PROGRAM_COLUMNS[key]).filter(Boolean)
}

export const getCardProgramColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(CARD_PROGRAM_STATUS_CONFIG[status as keyof typeof CARD_PROGRAM_STATUS_CONFIG] || CARD_PROGRAM_STATUS_CONFIG.all)
  return Object.entries(CARD_PROGRAM_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ['name', 'status'].includes(key),
    order: index
  }))
}
